<template>
  <div class="component item-list-summary summary-product">

      <img :src="item.imageUrl" alt="" class="image">

      <div class="info">
        <div class="name">{{ item.name }}</div>

        <div class="input">
          <div class="quantity">
            <div class="price">{{ displayPrice }}</div>
            <quantity-picker v-model="cartItem.quantity" :max="item.percentageType ? 1 : undefined"/>
          </div>

          <div class="button-add-container">
            <button class="button-add" :class="{ active: buttonActive }" @click="onUpdateCart">{{ buttonLabel }}</button>
            <!-- Cart Updated animation svg -->
            <div class="svg"  v-bind:class="{ updated: cartAnimationActive }">
              <svg x="0px" y="0px" viewBox="0 0 32 32">
                <path stroke-dasharray="19.79 19.79" stroke-dashoffset="19.79" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="square" stroke-miterlimit="10" d="M9,17l3.9,3.9c0.1,0.1,0.2,0.1,0.3,0L23,11"></path>
              </svg>
            </div>
          </div>
        </div>

        <div class="description" v-if="!showExcerpt" v-html="description"></div>
        <div class="description" v-else>
          <div v-if="!expandDescription" v-html="excerpt">
            <a class="more" @click="toggleExcerpt">{{ $t('general.label.more') }}</a>
          </div>
          <div v-if="expandDescription" v-html="description">
            <a class="less" @click="toggleExcerpt">{{ $t('general.label.less') }}</a>
          </div>
        </div>
      </div>

  </div>
</template>

<script>
import QuantityPicker from '@/components/QuantityPicker.vue';
import Cart from '@/models/Cart';
import CartItem from '@/models/CartItem';

export default {
  props: {
    item: {
      type: Object,
      required: true
    },
    excerptLength: {
      type: Number,
      default: 0
    },
  },


  data() {
    return {
      expandDescription: false,
      // this cartItem is the one that's actively edited before adding it to cart. See also itemInCart
      cartItem: this.createCartItem(),
      cartAnimationActive: false,
    }
  },

  computed: {
    description() {
      return this.item.description;
    },

    displayPrice() {
      return this.$formatter.currency(this.item.price);
    },

    showExcerpt() {
      return this.excerptLength > 0 && this.description.length > this.excerptLength;
    },

    excerpt() {
      return this.description.substring(0, this.excerptLength) + '...';
    },

    itemInCart() {
      return this.getItemInCart();
    },

    buttonActive() {
      let item = this.itemInCart;
      return item ? item.quantity != this.cartItem.quantity : this.cartItem.quantity > 0;
    },

    buttonLabel() {
      return this.itemInCart ? this.$t('form.label.update-cart') : this.$t('form.label.add-to-cart');
    }
  },

  methods: {
    onUpdateCart() {
      let existingItem = this.itemInCart;

      if (existingItem) {
        this.cartItem.id = existingItem.id;
      }

      this.handleButtonAnimation();

      Cart.update(this.cartItem).then(() => {
        // reset the cartItem
        this.cartItem = this.createCartItem();
      });
    },

    // this cart item is the one that might exist in the cart already
    getItemInCart() {
      return this.$cart.active.findItem(this.item.id, 'product');
    },

    createCartItem() {
      let item = this.getItemInCart();
      let qty = item ? item.quantity : 1;
      return new CartItem({ itemId: this.item.id, type: 'product', quantity: qty });
    },

    toggleExcerpt() {
      this.expandDescription = !this.expandDescription;
    },

    handleButtonAnimation() {
      this.cartAnimationActive = !this.cartAnimationActive;
      setTimeout(() => {
        this.cartAnimationActive = false;
      }, 2000)
    }
  },

  components: {
    QuantityPicker
  }
}
</script>

<style lang="scss">

.component.summary-product {
  display: grid;
  display: -ms-grid;
  grid-template-columns: 100px 1fr;
  -ms-grid-columns: 100px 1fr;
  grid-column-gap: 10px;
  @include breakpoint(medium) {
    grid-template-columns: 130px 1fr;
    -ms-grid-columns: 130px 1fr;
    grid-column-gap: 15px;
  }

  .image {
    object-fit: cover;
    width: 100px;
    height: 100px;
    -ms-grid-column: 1;
    @include breakpoint(medium) {
      width: 130px;
      height: 130px;
    }
  }

  .name {
    @include font-h3;
    margin-bottom: 10px;
    @include breakpoint(medium) {
      font-size: 18px;
    }
  }

  .quantity {
    display: grid;
    display: -ms-grid;
    grid-template-columns: auto 100px;
    -ms-grid-columns: auto 100px;
    grid-column-gap: 15px;
    margin-bottom: 10px;
    padding-right: 10px;
  }

  .price {
    @include font-h2;
    text-align: left;
    -ms-grid-column: 1;
    @include breakpoint(medium) {
      text-align: left;
    }

    /*  Edge */
    @supports (-ms-ime-align:auto) {
      margin-right: 20px;
    }

    /*  IE */
    @media all and (-ms-high-contrast:none) {
      margin-right: 20px;
    }
  }

  .component.quantity-picker {
    -ms-grid-column: 2;
  }

  .button-add-container {
    position: relative;
    overflow: hidden;
  }

  .button-add {
    @include inactive-button($colorCTA, #fff);
  }

  .svg {
    position: absolute;
    top: -45px;
    right: 0;
    height: 45px;
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
    background-color: $colorGreen;
    opacity: 0;
    transition: opacity .3s, top .1s ease .3s;
    /*  IE */
    @media all and (-ms-high-contrast:none) {
      display: none;
    }

    svg {
      width: 40px;
      height: 40px;
    }

    &.updated {
      top: 0;
      opacity: 1;
      transition: opacity .2s;

      svg path {
        stroke-dashoffset: 19.79px;
        animation: checkmark .2s ease-in-out .2s forwards;
      }

    }
  }

  @keyframes checkmark {
    0% { stroke-dashoffset: 19.79px; }
    100% { stroke-dashoffset: 0px; }
  }


  .info {
    /*  Edge */
    @supports (-ms-ime-align:auto) {
      margin-left: 15px;
    }

    /*  IE */
    -ms-grid-column: 2;
    @media all and (-ms-high-contrast:none) {
      margin-left: 15px;
    }

    .description {
      margin-top: 20px;
      margin-bottom: 0;

      .more {
        margin-left: 5px;
        &::after {
          @include font-awesome-pseudo($fa-var-chevron-down, light);
          margin-left: 5px;
        }
      }
      .less {
        margin-left: 5px;
        &::after {
          @include font-awesome-pseudo($fa-var-chevron-up, light);
          margin-left: 5px;
        }
      }
    }
  }

  // Desktop layout
  @include breakpoint(medium) {
    .info {
      @include clearfix;

      .name {
        float: left;
        width: calc(100% - 250px);
      }
      .description {
        float: left;
        margin-top: 10px;
        width: calc(100% - 250px);
      }
      .input {
        float: right;
        width: 230px;
        margin-left: 20px;
      }
    }
  }
}
</style>